import { useState } from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { FormError, FormInput } from '@/components/form';
import { Button } from '@/components/ui/button';
import { useCampaigns } from '@/hooks/useCampaigns';
import { formatDate } from '@/utils/format';

import { METRIC_DEFINITIONS } from '@repo/shared-types';

import type { CreateReportDto } from '@repo/shared-types';

const HOURS_KEYS = new Set([
  'total_hours_from_lead_generation_email',
  'total_hours_from_newcross_callbacks',
]);

// Group METRIC_DEFINITIONS by category (preserving order)
function getMetricsByCategory() {
  const groups: { category: string; metrics: (typeof METRIC_DEFINITIONS)[number][] }[] = [];
  const seen = new Map<string, (typeof METRIC_DEFINITIONS)[number][]>();
  for (const def of METRIC_DEFINITIONS) {
    if (!seen.has(def.category)) {
      const arr: (typeof METRIC_DEFINITIONS)[number][] = [];
      seen.set(def.category, arr);
      groups.push({ category: def.category, metrics: arr });
    }
    seen.get(def.category)!.push(def);
  }
  return groups;
}

const METRICS_BY_CATEGORY = getMetricsByCategory();

interface CreateReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (dto: CreateReportDto) => Promise<void>;
  isPending: boolean;
}

export function CreateReportDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: CreateReportDialogProps) {
  const [reportName, setReportName] = useState('');
  const [weekStartDate, setWeekStartDate] = useState('');
  const [weekEndDate, setWeekEndDate] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [hoursLeadGen, setHoursLeadGen] = useState('');
  const [hoursNewcross, setHoursNewcross] = useState('');
  const [formError, setFormError] = useState('');
  const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(
    () => new Set(METRIC_DEFINITIONS.map((m) => m.key)),
  );

  // Fetch campaigns for the dropdown
  const { data: campaignsData } = useCampaigns({
    limit: 100,
    sortBy: 'runs_at',
    sortOrder: 'desc',
  });

  const toggleMetric = (key: string) => {
    setSelectedMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleCategory = (category: string) => {
    const group = METRICS_BY_CATEGORY.find((g) => g.category === category);
    if (!group) return;
    const allSelected = group.metrics.every((m) => selectedMetrics.has(m.key));
    setSelectedMetrics((prev) => {
      const next = new Set(prev);
      for (const m of group.metrics) {
        if (allSelected) {
          next.delete(m.key);
        } else {
          next.add(m.key);
        }
      }
      return next;
    });
  };

  const resetForm = () => {
    setReportName('');
    setWeekStartDate('');
    setWeekEndDate('');
    setCampaignId('');
    setHoursLeadGen('');
    setHoursNewcross('');
    setFormError('');
    setSelectedMetrics(new Set(METRIC_DEFINITIONS.map((m) => m.key)));
  };

  const handleSubmit = async () => {
    setFormError('');

    if (!reportName.trim()) {
      setFormError('Report name is required');
      return;
    }

    // Either campaign OR date range must be provided
    if (!campaignId && (!weekStartDate || !weekEndDate)) {
      setFormError('Please either select a campaign OR provide both start and end dates');
      return;
    }

    let startDate: Date;
    let endDate: Date;

    if (campaignId) {
      // If campaign is selected, use its date
      const campaign = campaignsData?.data?.find((c) => c.id === campaignId);
      if (!campaign) {
        setFormError('Selected campaign not found');
        return;
      }
      startDate = new Date(campaign.runsAt);
      endDate = new Date(campaign.runsAt);
    } else {
      // Use provided dates
      startDate = new Date(weekStartDate);
      endDate = new Date(weekEndDate);

      if (endDate < startDate) {
        setFormError('End date must be on or after start date');
        return;
      }
    }

    if (selectedMetrics.size === 0) {
      setFormError('At least one metric must be selected');
      return;
    }

    if (hoursLeadGen && selectedMetrics.has('total_hours_from_lead_generation_email')) {
      const hoursLeadGenNum = parseFloat(hoursLeadGen);
      if (isNaN(hoursLeadGenNum) || hoursLeadGenNum < 0) {
        setFormError('Lead generation hours must be a valid non-negative number');
        return;
      }
    }

    if (hoursNewcross && selectedMetrics.has('total_hours_from_newcross_callbacks')) {
      const hoursNewcrossNum = parseFloat(hoursNewcross);
      if (isNaN(hoursNewcrossNum) || hoursNewcrossNum < 0) {
        setFormError('Newcross callback hours must be a valid non-negative number');
        return;
      }
    }

    try {
      await onSubmit({
        name: reportName,
        week_start_date: startDate,
        week_end_date: endDate,
        campaign_id: campaignId || undefined,
        total_hours_from_lead_generation_email:
          hoursLeadGen && selectedMetrics.has('total_hours_from_lead_generation_email')
            ? parseFloat(hoursLeadGen)
            : undefined,
        total_hours_from_newcross_callbacks:
          hoursNewcross && selectedMetrics.has('total_hours_from_newcross_callbacks')
            ? parseFloat(hoursNewcross)
            : undefined,
        selected_metrics: Array.from(selectedMetrics),
      });
      resetForm();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      setFormError(err?.response?.data?.message ?? 'Failed to generate report. Please try again.');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 bg-black/50" />
        <Dialog.Content className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-[50%] left-[50%] z-50 max-h-[90vh] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] overflow-y-auto rounded-lg bg-white p-6 shadow-lg dark:bg-slate-900">
          <Dialog.Title className="mb-2 text-lg font-semibold">Generate New Report</Dialog.Title>
          <Dialog.Description className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            Create a report for a specific campaign OR for a custom date range across all campaigns.
          </Dialog.Description>

          <div className="space-y-4">
            <div>
              <label htmlFor="reportName" className="mb-1 block text-sm font-medium">
                Report Name <span className="text-red-500">*</span>
              </label>
              <FormInput
                id="reportName"
                type="text"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                placeholder="e.g., Campaign Report - January 2026"
              />
            </div>
            <div>
              <label htmlFor="campaignId" className="mb-1 block text-sm font-medium">
                Campaign
              </label>
              <select
                id="campaignId"
                value={campaignId}
                onChange={(e) => {
                  setCampaignId(e.target.value);
                  // Clear dates when campaign is selected
                  if (e.target.value) {
                    setWeekStartDate('');
                    setWeekEndDate('');
                  }
                }}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-white"
              >
                <option value="">Select a campaign or use date range below</option>
                {campaignsData?.data?.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name} ({formatDate(campaign.runsAt)})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Select a campaign to generate a report for that specific campaign date
              </p>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-slate-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-2 text-gray-500 dark:bg-slate-900 dark:text-gray-400">
                  OR
                </span>
              </div>
            </div>

            <div>
              <label
                htmlFor="weekStartDate"
                className={`mb-1 block text-sm font-medium ${campaignId ? 'text-gray-400 dark:text-gray-600' : ''}`}
              >
                Start Date
              </label>
              <FormInput
                id="weekStartDate"
                type="date"
                value={weekStartDate}
                onChange={(e) => {
                  setWeekStartDate(e.target.value);
                  // Clear campaign when dates are entered
                  if (e.target.value && campaignId) {
                    setCampaignId('');
                  }
                }}
                placeholder="Select start date"
                disabled={!!campaignId}
              />
            </div>
            <div>
              <label
                htmlFor="weekEndDate"
                className={`mb-1 block text-sm font-medium ${campaignId ? 'text-gray-400 dark:text-gray-600' : ''}`}
              >
                End Date
              </label>
              <FormInput
                id="weekEndDate"
                type="date"
                value={weekEndDate}
                onChange={(e) => {
                  setWeekEndDate(e.target.value);
                  // Clear campaign when dates are entered
                  if (e.target.value && campaignId) {
                    setCampaignId('');
                  }
                }}
                placeholder="Select end date"
                disabled={!!campaignId}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {campaignId
                  ? 'Date fields disabled - using selected campaign date'
                  : 'Provide a date range to include data from all campaigns within that period'}
              </p>
            </div>

            {/* Metrics Selection */}
            <div className="border-t pt-4 dark:border-slate-700">
              <p className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">Metrics</p>
              <div className="space-y-4">
                {METRICS_BY_CATEGORY.map((group) => {
                  const allSelected = group.metrics.every((m) => selectedMetrics.has(m.key));
                  const someSelected = group.metrics.some((m) => selectedMetrics.has(m.key));
                  return (
                    <div key={group.category}>
                      <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someSelected && !allSelected;
                          }}
                          onChange={() => toggleCategory(group.category)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800"
                        />
                        {group.category}
                      </label>
                      <div className="mt-1.5 ml-6 space-y-1.5">
                        {group.metrics.map((metric) => {
                          const isHoursField = HOURS_KEYS.has(metric.key);
                          const isChecked = selectedMetrics.has(metric.key);
                          return (
                            <div key={metric.key}>
                              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleMetric(metric.key)}
                                  className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800"
                                />
                                {metric.label}
                              </label>
                              {isHoursField && isChecked && (
                                <div className="mt-1 ml-6">
                                  <FormInput
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    value={
                                      metric.key === 'total_hours_from_lead_generation_email'
                                        ? hoursLeadGen
                                        : hoursNewcross
                                    }
                                    onChange={(e) =>
                                      metric.key === 'total_hours_from_lead_generation_email'
                                        ? setHoursLeadGen(e.target.value)
                                        : setHoursNewcross(e.target.value)
                                    }
                                    placeholder="0.0"
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {formError && <FormError message={formError} />}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isPending}>
              {isPending ? 'Generating...' : 'Generate Report'}
            </Button>
          </div>

          <Dialog.Close asChild>
            <button
              className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
